var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { createUseStyles } from 'react-jss';
import { useTheme } from '../../theme';
import { useMemoClasses } from '../../utils/hooks';
var useStyles = createUseStyles({
    base: { margin: '0 0 var(--RUI-padding) 0' },
    baseOverrides: function (theme) { return (__assign({}, theme.overrides.Text.base)); },
    primary: { color: 'var(--RUI-color-primary)' },
    secondary: { color: 'var(--RUI-color-secondary)' },
    neutral: { color: 'var(--RUI-color-neutral)' },
    error: { color: 'var(--RUI-color-error)' },
    success: { color: 'var(--RUI-color-success)' },
    warning: { color: 'var(--RUI-color-warning)' },
    inherit: { color: 'inherit' },
    title1: { font: 'var(--RUI-font-title1)' },
    title1Overrides: function (theme) { return (__assign({}, theme.overrides.Text.title1)); },
    title2: { font: 'var(--RUI-font-title2)' },
    title2Overrides: function (theme) { return (__assign({}, theme.overrides.Text.title2)); },
    title3: { font: 'var(--RUI-font-title3)' },
    title3Overrides: function (theme) { return (__assign({}, theme.overrides.Text.title3)); },
    title4: { font: 'var(--RUI-font-title4)' },
    title4Overrides: function (theme) { return (__assign({}, theme.overrides.Text.title4)); },
    title5: { font: 'var(--RUI-font-title5)' },
    title5Overrides: function (theme) { return (__assign({}, theme.overrides.Text.title5)); },
    body1: { font: 'var(--RUI-font-body1)' },
    body1Overrides: function (theme) { return (__assign({}, theme.overrides.Text.body1)); },
    body2: { font: 'var(--RUI-font-body2)' },
    body2Overrides: function (theme) { return (__assign({}, theme.overrides.Text.body2)); },
    caption: { font: 'var(--RUI-font-caption)', marginBottom: 0 },
    captionOverrides: function (theme) { return (__assign({}, theme.overrides.Text.caption)); },
    button: {
        font: 'var(--RUI-font-button)',
        textTransform: 'uppercase',
        marginBottom: 0,
    },
    buttonOverrides: function (theme) { return (__assign({}, theme.overrides.Text.button)); },
    underlined: { textDecoration: 'underline' },
    underlinedOverrides: function (theme) { return (__assign({}, theme.overrides.Text.underlined)); },
    strong: { fontWeight: 700 },
    strongOverrides: function (theme) { return (__assign({}, theme.overrides.Text.strong)); },
    light: { fontWeight: 300 },
    lightOverrides: function (theme) { return (__assign({}, theme.overrides.Text.light)); },
    crossed: { textDecoration: 'line-through' },
    crossedOverrides: function (theme) { return (__assign({}, theme.overrides.Text.crossed)); },
    highlighted: { backgroundColor: 'var(--RUI-color-warning)' },
    highlightedOverrides: function (theme) { return (__assign({}, theme.overrides.Text.highlighted)); },
    keyboard: {
        padding: 'calc(0.1 * var(--RUI-padding)) calc(0.4 * var(--RUI-padding)) calc(0.3 * var(--RUI-padding)) calc(0.1 * var(--RUI-padding))',
        fontFamily: 'monospace',
        border: 'var(--RUI-border-width) solid var(--RUI-color-paper-darker)',
        borderRadius: '10%',
        borderBottomWidth: 'calc(var(--RUI-border-width) * 2)',
        backgroundColor: 'var(--RUI-color-paper-dark)',
    },
    keyboardOverrides: function (theme) { return (__assign({}, theme.overrides.Text.keyboard)); },
}, { name: 'RUIText' });
var defaultTags = {
    title1: 'h1',
    title2: 'h2',
    title3: 'h3',
    title4: 'h4',
    title5: 'h5',
    body1: 'p',
    body2: 'p',
    caption: 'span',
    button: 'span',
};
var Text = function (_a) {
    var variant = _a.variant, tag = _a.tag, color = _a.color, underlined = _a.underlined, strong = _a.strong, light = _a.light, crossed = _a.crossed, highlighted = _a.highlighted, keyboard = _a.keyboard, children = _a.children, className = _a.className, props = __rest(_a, ["variant", "tag", "color", "underlined", "strong", "light", "crossed", "highlighted", "keyboard", "children", "className"]);
    var theme = useTheme();
    var styles = useStyles(theme);
    var classes = useMemoClasses([
        styles[variant],
        styles.base,
        styles[color],
        theme.overrides.Text.base && styles.baseOverrides,
        variant === 'title1' && theme.overrides.Text.title1 && styles.title1Overrides,
        variant === 'title2' && theme.overrides.Text.title2 && styles.title2Overrides,
        variant === 'title3' && theme.overrides.Text.title3 && styles.title3Overrides,
        variant === 'title4' && theme.overrides.Text.title4 && styles.title4Overrides,
        variant === 'title5' && theme.overrides.Text.title5 && styles.title5Overrides,
        variant === 'body1' && theme.overrides.Text.body1 && styles.body1Overrides,
        variant === 'body2' && theme.overrides.Text.body2 && styles.body2Overrides,
        variant === 'button' && theme.overrides.Text.button && styles.buttonOverrides,
        variant === 'caption' && theme.overrides.Text.caption && styles.captionOverrides,
        underlined && styles.underlined,
        underlined && theme.overrides.Text.underlined && styles.underlinedOverrides,
        strong && styles.strong,
        strong && theme.overrides.Text.strong && styles.strongOverrides,
        light && styles.light,
        light && theme.overrides.Text.light && styles.lightOverrides,
        crossed && styles.crossed,
        crossed && theme.overrides.Text.crossed && styles.crossedOverrides,
        highlighted && styles.highlighted,
        highlighted && theme.overrides.Text.highlighted && styles.highlightedOverrides,
        keyboard && styles.keyboard,
        keyboard && theme.overrides.Text.keyboard && styles.keyboardOverrides,
        'RUIText-base',
        className,
    ], [theme, variant, color, underlined, strong, light, crossed, highlighted, className, keyboard]);
    var Tag = tag || defaultTags[variant];
    return (React.createElement(Tag, __assign({ className: classes }, props), children));
};
Text.defaultProps = {
    variant: 'body1',
    tag: '',
    color: 'inherit',
    underlined: false,
    strong: false,
    light: false,
    crossed: false,
    highlighted: false,
};
export { Text as default, Text };
